import type { ApiClient } from '@/lib/api'

import { readOfferingDetail } from './offerings'
import { readAMLCase } from './aml'
import { readScreeningTrail } from './screening'
import { openCandidate } from './catalogcontrols'
import { readCopyKeyHistory } from './copy'
import { readCaller } from './handles'
import { readHeldHandle } from './handleclaims'
import { readHelpArticleVersions, readTroubleshooterVersions } from './help'
import { readCustomerPriceList, readPriceListVersion } from './pricing'
import { readPartnerDetail } from './referrals'
import { readAccessReviews } from './staff'
import { readCashOut } from './cashflow'
import { readDiscrepancyCase, readReplayRun } from './ledger_recon'
import { readGuidanceHistory } from './payoutguidance'
import { readProviderObservations } from './providers'
import { readStatementPeriod } from './statements_referrals'
import { readStorefrontOrder } from './storefront'
import type { ScreenData } from './provenance'

/**
 * What a row on a screen OPENS INTO.
 *
 * # Why this is a registry rather than a branch in the console
 *
 * `Console.tsx` had one, hard-coded: `screenId === 'rk-aml'` fetched a case and
 * rendered it. That was right for one screen and wrong as a shape — the second
 * openable screen would have been a second branch, the third a third, and the
 * rule about WHEN a row may be opened (only over a genuinely populated live
 * read) would have been restated each time with a chance to get it wrong.
 *
 * The rule lives once, in the console. What each screen opens into lives here,
 * and a reviewer can read the whole answer in the literal below.
 *
 * # These are not screen loaders and must never be registered as one
 *
 * Same reasoning as `readDecision`: an opener hangs off a ROW. Registering one
 * in `SCREEN_LOADERS` would claim some screen is "the AML case screen" or "the
 * attestation screen", and there is no such screen — there is a queue, and a
 * row in it that opens.
 *
 * # A screen absent from here is a screen whose rows do not open
 *
 * Which is the default and the honest one. `rowKeys` on a screen with no opener
 * costs nothing and claims nothing; a row that appeared to open and did not
 * would be worse than a row that plainly does not.
 */
export type RowOpener = (api: ApiClient, key: string) => Promise<ScreenData>

/**
 * Screen id → what its selected row opens.
 *
 * Deliberately a flat literal, for the reason `SCREEN_LOADERS` is one: the
 * answer to "which rows in this console open into something" should be a list
 * somebody reads, not the result of running the app.
 */
export const ROW_OPENERS: Readonly<Record<string, RowOpener>> = {
  // The AML case and its append-only trail (#46). The queue says a case needs a
  // human; this is the case.
  'rk-aml': async (api, key) => (await readAMLCase(api, key)).data,

  // One subject's whole screening trail (#170). Keyed `provider@provider_ref`
  // because a screening row is addressed by both — a reference alone names a
  // subject at one vendor, and the same person screened by two vendors is two
  // trails that must not merge.
  'rk-screening': readScreeningTrail,

  // One person's attestation history (#77). The directory says WHEN somebody
  // was last confirmed; this says what was confirmed each time — and the
  // difference between the roles snapshotted then and the roles held now is the
  // finding an access review exists to produce.
  'ac-staff': readAccessReviews,
  'ac-reviews': readAccessReviews,

  // One offering's version history AND its tariff, as TWO reads (#178).
  //
  // Two rather than one because they are two permissions: the routing table is
  // Ops' and what the rail costs us is Finance's. An Ops session gets the
  // second refused, and the opener renders that as a sentence naming Finance
  // rather than as an error or an empty table — an empty buy-price table reads
  // as "no buy price", which is a real and consequential state ("unknown, never
  // free") that a permission boundary must not manufacture.
  //
  // The opener is what makes the six controls checkable. A publish with nothing
  // that lists what was published is the `pr-assign` defect from the other end:
  // a control is fabricated capability unless an operator can observe its
  // effect (go-wallet#862).
  'pv-offerings': readOfferingDetail,

  // One fee schedule's priced lines (#66). The catalogue row carries
  // `code@version`, because a schedule is addressed by both — a code alone
  // names a history rather than a set of prices.
  'pr-lists': readPriceListVersion,

  // One partner's whole conduct history (#79), including RESOLVED flags and
  // LIFTED suspensions. A partner flagged twice and cleared twice looks
  // identical to one nobody has ever questioned if only live rows are shown,
  // and it is the second whose record should read as clean.
  //
  // It also carries the self-billing invoices raised on that partner's behalf
  // (#109). `rf-payouts` used to open into one and no longer opens at all: the
  // approval queue returns only payouts awaiting approval, an invoice exists
  // only for one already paid, and the backend refuses to key the read by
  // payout for exactly that reason. A row that appeared to open into a document
  // it could never have is worse than a row that plainly does not open.
  'rf-partners': readPartnerDetail,

  // What prices one customer, and which rung decided (#83). Answered by the
  // same resolver the fee path calls, so this screen cannot disagree with what
  // they are actually charged.
  'cust-list': readCustomerPriceList,

  // One content key's whole history, in every language (#97). Drafts,
  // rejections and superseded versions included: a version number is never
  // reused, so a gap in the sequence is a rejection worth seeing.
  'ct-help': readHelpArticleVersions,
  'ct-trees': readTroubleshooterVersions,

  // Every sentence one key has had, and who ended each one (#99). An override
  // is closed rather than edited, so the closure is the half the catalogue of
  // current sentences cannot show.
  'ct-templates': readCopyKeyHistory,

  // The claim behind a held handle, and the decision on it (#111). This is the
  // only opener whose blocks include a control: the queue says a name is held,
  // and this is the name, the ruleset that held it, and the one act an operator
  // may take about it.
  //
  // The caller's permission closure is read here rather than passed in, because
  // `RowOpener` is `(api, key)` — and it is read at all because the decide
  // control is gated on `handle.impersonation.decide`, which is granted to
  // Compliance and to nobody else. A `/me` that failed leaves the evidence
  // readable with the control disabled and the reason stated, which is the safe
  // direction and the one §6.18.1 asks for.
  'hd-reviews': async (api, key) => readHeldHandle(api, key, await readCaller(api)),

  // One storefront order and every transition recorded against it (#136).
  //
  // This is where the queue's absent columns actually live. The designed row
  // draws a supplier error and a refund state; neither is a field on the queue
  // read, and the per-event `reason_code`, `detail` and `external_ref` that
  // answer for them are on the order's own history. It is also the only read in
  // this build that returns an order in ANY state — the queue serves the owed
  // state alone — so an order that has since been delivered or refunded is
  // reachable here and nowhere else.
  'sf-failed': readStorefrontOrder,

  // One institution's probe history (#144), which is what the verdict cannot
  // answer. A provider reading `unknown` because its verdict went stale is one
  // of two entirely different situations — our heartbeats stopped arriving, or
  // they kept arriving and started reporting the institution unreachable — and
  // both render identically on a screen showing only the current status.
  // Reading the gap in this history is how an operator tells them apart.
  'pv-health': readProviderObservations,

  // One discrepancy case, with both records side by side (#147). A D3 case IS
  // the two sides disagreeing, and a table row cannot show that — it has one
  // amount column and the case has two figures in two currencies. This is also
  // where the stored proposal is stated as arithmetic nobody has run.
  'lg-disc': readDiscrepancyCase,

  // One rebuild's divergences (#147). The run row says how many accounts
  // disagreed; this says which, and which side of each does not exist — the
  // distinction between the journal justifying a balance the projection does
  // not hold and the projection holding one no posting accounts for.
  'lg-replay': readReplayRun,

  // One outbound payment (#147), with the idempotency key an operator quotes to
  // the institution and the decision reference they quote to the customer.
  'mny-cashouts': readCashOut,

  // Every version of one platform-and-language's payout guidance (#172). The
  // list shows what a customer is reading now; this shows what they were reading
  // when they followed it, which is the question a complaint arrives as.
  'ct-payout': readGuidanceHistory,

  // Every pass over one billing period (#172), keyed by the period rather than
  // the run: `st-versions` in the design. A period passed twice with different
  // counts is the row this opens for — and the designed content hash is NOT
  // here, because no read on this surface serves one.
  'st-runs': readStatementPeriod,

  // One catalog candidate, its evidence, and the two decisions on it (#204).
  //
  // The second opener whose blocks include controls, and the reason it is an
  // opener at all: the queue is a hundred rows and the design draws ONE
  // composer, singular. Two controls per row would be two hundred controls
  // under a table nobody reads to the bottom of.
  //
  // It re-reads the queue rather than trusting the row it was opened from.
  // There is no per-candidate GET, and repeating the list read is better than a
  // workaround for one: the endpoint serves only candidates nobody has decided,
  // so one somebody else promoted while this page was open is simply absent —
  // which is a live answer at the moment of the decision rather than a 409
  // after the operator has composed the whole thing.
  'rc-cand': openCandidate,
}

export function openerFor(screenId: string): RowOpener | undefined {
  return ROW_OPENERS[screenId]
}
